import { useRef, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Scene } from '../3d/Scene';
import { MagneticButton } from '../ui/MagneticButton';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function Hero() {
    const containerRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLHeadingElement>(null);

    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 500], [0, 200]);

    // Scroll indicator fades out and blurs as user scrolls
    const scrollIndicatorOpacity = useTransform(scrollY, [0, 150], [1, 0]);
    const scrollIndicatorBlur = useTransform(scrollY, [0, 150], [0, 10]);

    useEffect(() => {
        if (textRef.current) {
            // Split text animation logic if needed, but for now simple fade up
            gsap.fromTo(textRef.current.children,
                { y: 100, opacity: 0 },
                { y: 0, opacity: 1, stagger: 0.1, duration: 1, ease: 'power3.out' }
            );
        }
    }, []);

    return (
        <section ref={containerRef} className="relative min-h-[100dvh] w-full overflow-hidden flex flex-col items-center">
            {/* 3D Background */}
            <Scene />

            {/* Content — centered with safe top padding for fixed header */}
            <motion.div
                style={{ y }}
                className="relative z-10 flex flex-col items-center justify-center flex-1 w-full px-4 pt-20"
            >
                <h1 ref={textRef} className="text-[clamp(3rem,12vw,8rem)] leading-[0.85] font-black tracking-tighter mix-blend-difference text-white selection:bg-emerald-500/50">
                    <div className="overflow-hidden">PARAS</div>
                    <div className="overflow-hidden text-emerald-500">LEHANA</div>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.8 }}
                    className="mt-4 md:mt-6 text-lg md:text-2xl text-gray-400 max-w-xl md:max-w-2xl mx-auto font-light text-center"
                >
                    Autonomous Architect & Full Stack Engineer<br className="hidden md:block" />
                    Building <span className="text-emerald-400 font-semibold">Next-Level</span> Digital Experiences
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.5, duration: 0.5 }}
                    className="mt-6 md:mt-8 flex flex-col md:flex-row justify-center gap-4 md:gap-6 w-full max-w-sm md:max-w-none"
                >
                    <MagneticButton className="bg-emerald-500 text-black hover:bg-emerald-400 w-full md:w-auto justify-center">
                        View Projects
                    </MagneticButton>
                    <MagneticButton className="border border-white/20 text-white hover:bg-white/10 w-full md:w-auto justify-center">
                        Contact Me
                    </MagneticButton>
                </motion.div>

                {/* Scroll Indicator — compact spacing (80% reduction) */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2, duration: 1 }}
                    style={{
                        opacity: scrollIndicatorOpacity,
                        filter: useTransform(scrollIndicatorBlur, (v) => `blur(${v}px)`),
                    }}
                    className="mt-3 md:mt-4 flex flex-col items-center gap-1"
                >
                    <span className="text-xs uppercase tracking-[0.2em] text-emerald-500/50">Scroll</span>
                    <div className="w-[1px] h-8 bg-gradient-to-b from-emerald-500 to-transparent animate-pulse" />
                </motion.div>
            </motion.div>

        </section>
    );
}

